import { pipToDecimal } from '@katanaperps/katana-perps-sdk';

import { useWalletWithPositionsQuery } from '#/api/queries/walletPositionsQueries';
import config from '#/config';
import { ETH_ADDRESS_ZERO } from '#/config/constants';
import {
  durationDaysHoursMinutesSeconds,
  formatDurationAsPeriod,
} from '#/lib/date';
import { formatNumber2, formatPercentage } from '#/lib/display/formatting';
import { getUUIDv1Timestamp } from '#/lib/string';
import { useSelector } from '#/reduxState/storeCore';

import { ApiVault, ApiVaultType } from './types';

import type { RemainingTime } from '#/lib/date';

export const isVaultManager = (walletAddress: string, vault: ApiVault) =>
  !!walletAddress &&
  vault.manager.toLowerCase() === walletAddress.toLowerCase();

export const useOwnVaultQuery = (
  enabled = true
): { name?: string; manager: string } | false | undefined => {
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const { data: walletData } = useWalletWithPositionsQuery(enabled);
  if (!walletData) {
    return undefined;
  }
  return walletData?.managedAccountProvider
    ? {
        name: walletData.vaultName,
        manager: walletAddress,
      }
    : false;
  // const walletManagedVault = useMemo(() => {
  //   return !vaultsResponse
  //     ? undefined
  //     : vaultsResponse.vaults.find(vault =>
  //         isVaultManager(walletAddress, vault)
  //       ) || false;
  // }, []);
  // return walletManagedVault;
};

export const getVaultTypeLabel = (vaultType: ApiVaultType | undefined) =>
  vaultType === 'fixedIncomeVaultV1'
    ? 'Fixed Income v1'
    : vaultType === 'profitShareVaultV1'
    ? 'Profit Share v1'
    : vaultType || '';

/** Type name without the contract version, for the creation flow copy */
export const getVaultTypeName = (vaultType: ApiVaultType | undefined) =>
  vaultType === 'fixedIncomeVaultV1'
    ? 'Fixed Income'
    : vaultType === 'profitShareVaultV1'
    ? 'Profit Share'
    : vaultType || '';

/**
 * Provider contract a new vault of the given type is created under - the
 * active provider instance per environment from config (cross-checked
 * against the exchange API by environments.test). Undefined when the
 * provider is not deployed / configured on the current environment, or when
 * creation of the type is explicitly disabled there
 * (`profitShareVaultCreationDisabled`).
 */
export function getVaultCreationProviderAddress(
  vaultType: ApiVaultType | undefined
): string | undefined {
  const { katanaChain } = config.chains;
  const address =
    vaultType === 'fixedIncomeVaultV1'
      ? katanaChain.fixedIncomeVaultProviderAddress
      : vaultType === 'profitShareVaultV1' &&
        !katanaChain.profitShareVaultCreationDisabled
      ? katanaChain.profitShareVaultProviderAddress
      : undefined;
  return address && address !== ETH_ADDRESS_ZERO ? address : undefined;
}

export const formatVaultCollateralization = (
  collateralizationFromAPI?: string
) => {
  if (!collateralizationFromAPI) {
    return '';
  }
  const collateralizationNum = Number(collateralizationFromAPI) * 100;
  return collateralizationNum > 1000
    ? '1000+%'
    : `${formatPercentage(collateralizationNum)}%`;
};

export function calculateVaultTresholds(
  configFieldsTransformed:
    | {
        minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal: string;
        maximumTotalOwedQuantityAvailableMultiplierToInitiateExit: string;
      }
    | undefined,
  summaryData: {
    totalOwedQuantity: bigint | undefined;
  }
) {
  const totalOwedQuantity =
    summaryData?.totalOwedQuantity !== undefined
      ? pipToDecimal(summaryData.totalOwedQuantity)
      : undefined;
  const minMultiplier =
    configFieldsTransformed?.minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal;
  const maxMultiplier =
    configFieldsTransformed?.maximumTotalOwedQuantityAvailableMultiplierToInitiateExit;
  const withdrawalThresholdValue =
    totalOwedQuantity !== undefined && minMultiplier !== undefined
      ? Number(totalOwedQuantity) * Number(minMultiplier)
      : undefined;
  const exitThresholdValue =
    totalOwedQuantity !== undefined && maxMultiplier !== undefined
      ? Number(totalOwedQuantity) * Number(maxMultiplier)
      : undefined;
  return {
    exitThresholdValue,
    withdrawalThresholdValue,
  };
}

/** Withdrawal Exit Age config value (seconds) as hours with up to 2 decimals,
 *  e.g. "24" or "375.65" - on-chain values need not be whole hours */
export const formatVaultExitAgeHours = (exitAgeInS: bigint | number | string) =>
  formatNumber2(Number(exitAgeInS) / 3600, 2, 0);

/**
 * Pips multiplier (0.1 = 10%) as percent with its full on-chain precision -
 * per-period management fees can be far below 0.01%
 */
export const formatVaultMultiplierPercent = (multiplier: string | number) =>
  `${formatNumber2(Number(multiplier) * 100, 8, 2, false)}%`;

/**
 * Period the Profit Share management fee rate refers to ("year" = annualised
 * fee) - a provider contract constant kept in config (verified by
 * environments.test), displayed as "1.00% / year"
 */
export const vaultManagementFeePeriodLabel = formatDurationAsPeriod(
  Number(config.chains.katanaChain.profitShareVaultManagementFeePeriodInS)
);

/**
 * Time left until the OLDEST queued withdrawal has stayed unserviced longer
 * than the configured Withdrawal Exit Age, after which depositors may initiate
 * a vault exit (`ended: true` once that point is passed).
 * Undefined when the config is unknown or nothing is queued.
 *
 * Informational only - unapplied deposits count toward the contract's age
 * condition too, and actual eligibility is simulated in useVaultExitEligibility.
 */
export function calculateVaultExitAgeRemaining(
  exitAgeInS: bigint | undefined,
  withdrawalQueue: { nonce: bigint }[] | undefined,
  nowMs: number
): RemainingTime | undefined {
  if (exitAgeInS === undefined || !withdrawalQueue?.length) {
    return undefined;
  }
  // Picked by nonce timestamp rather than array position so the result does
  // not depend on the aggregator's queue ordering
  const oldestRequestedAt = Math.min(
    ...withdrawalQueue.map(item => getUUIDv1Timestamp(item.nonce))
  );
  return durationDaysHoursMinutesSeconds(
    oldestRequestedAt + Number(exitAgeInS) * 1000 - nowMs
  );
}

/** Compact "1d 4h" / "5h 12m" / "3m" for exit age countdowns */
export const formatVaultExitAgeRemaining = (remaining: RemainingTime) =>
  remaining.days > 0
    ? `${remaining.days}d ${remaining.hours}h`
    : remaining.hours > 0
    ? `${remaining.hours}h ${remaining.minutes}m`
    : `${Math.max(remaining.minutes, 1)}m`;

export const vaultCreatedUrl = (vaultManager: string) =>
  `/vaults/${vaultManager}#vaultCreated`;

/**
 * Determines the label type and text based on time difference
 * Used for displaying withdrawal processing time or age of withdrawal requests
 */
export function getVaultWithdrawalTimeDiffLabel(diff: RemainingTime): {
  type: 'error' | 'warning' | 'success';
  text: string;
} | null {
  if (diff.days > 0) {
    return {
      type: 'error',
      text: `${diff.days} ${diff.days === 1 ? 'day' : 'days'}`,
    };
  }
  if (diff.hours > 0) {
    return {
      type: 'error',
      text: `${diff.hours} ${diff.hours === 1 ? 'hour' : 'hours'}`,
    };
  }
  if (diff.minutes > 0) {
    return {
      type: diff.minutes > 5 ? 'warning' : 'success',
      text: `${diff.minutes} min`,
    };
  }
  // Less than a minute
  return {
    type: 'success',
    text: '< 1 min',
  };
}
